import React, { useEffect, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api from "@/lib/api";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { METHOD_LABEL } from "@/components/wallet/shared";

const EMPTY = { amount: "", method: "bonifico", reference: "", note: "", file: null };

export default function TopupRequestDialog({ open, onOpenChange }) {
  const qc = useQueryClient();
  const [f, setF] = useState(EMPTY);
  const { data: cfg } = useQuery({ queryKey: ["wallet-config"], queryFn: async () => (await api.get("/wallet/config")).data, enabled: open });
  const methods = Object.entries(METHOD_LABEL).filter(([k]) => !cfg || (cfg.enabled_methods || []).includes(k));
  useEffect(() => { if (methods.length && !methods.some(([k]) => k === f.method)) setF((p) => ({ ...p, method: methods[0][0] })); }, [cfg]); // eslint-disable-line react-hooks/exhaustive-deps
  const mut = useMutation({
    mutationFn: async () => {
      const fd = new FormData();
      fd.append("amount", f.amount);
      fd.append("method", f.method);
      if (f.reference) fd.append("reference", f.reference);
      if (f.note) fd.append("note", f.note);
      if (f.file) fd.append("file", f.file);
      return (await api.post("/wallet/me/topups", fd)).data;
    },
    onSuccess: () => { toast.success("Richiesta inviata all'economato"); qc.invalidateQueries({ queryKey: ["my-wallet"] }); setF(EMPTY); onOpenChange(false); },
    onError: (e) => toast.error(e.response?.data?.detail || "Errore invio"),
  });
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md max-h-[92vh] overflow-y-auto">
        <DialogHeader><DialogTitle>Richiedi una ricarica</DialogTitle></DialogHeader>
        <ol className="text-xs text-stone-600 list-decimal pl-4 space-y-0.5">
          <li>Effettua il pagamento con il metodo che preferisci.</li>
          <li>Compila il modulo e allega la ricevuta.</li>
          <li>L'economato verifica e accredita il wallet.</li>
        </ol>
        {cfg?.payment_instructions && (
          <div className="p-3 rounded-lg bg-[#0F766E]/5 border border-[#0F766E]/20 text-xs whitespace-pre-line" data-testid="topup-instructions">{cfg.payment_instructions}</div>
        )}
        <div>
          <Label className="text-xs">Metodo di pagamento</Label>
          <div className="flex gap-1.5 flex-wrap mt-1.5">
            {methods.map(([k, l]) => (
              <button key={k} type="button" onClick={() => setF({ ...f, method: k })} data-testid={`topup-method-${k}`}
                className={`px-3 py-1.5 rounded-full text-xs border transition-colors ${f.method === k ? "bg-[#0F766E] text-white border-[#0F766E]" : "bg-white border-stone-200"}`}>{l}</button>
            ))}
          </div>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div><Label className="text-xs">Importo (€)</Label><Input type="number" min="1" step="0.01" value={f.amount} onChange={(e) => setF({ ...f, amount: e.target.value })} className="mt-1.5" data-testid="topup-amount"/></div>
          <div><Label className="text-xs">Riferimento</Label><Input value={f.reference} onChange={(e) => setF({ ...f, reference: e.target.value })} placeholder="CRO / ID transazione" className="mt-1.5" data-testid="topup-reference"/></div>
        </div>
        <div><Label className="text-xs">Ricevuta (PDF o immagine, max 10 MB)</Label><Input type="file" accept=".pdf,image/*" onChange={(e) => setF({ ...f, file: e.target.files?.[0] || null })} className="mt-1.5" data-testid="topup-file"/></div>
        <div><Label className="text-xs">Nota</Label><Input value={f.note} onChange={(e) => setF({ ...f, note: e.target.value })} className="mt-1.5" data-testid="topup-note"/></div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Annulla</Button>
          <Button className="bg-[#0F766E] hover:bg-[#115E59]" disabled={!(parseFloat(f.amount) > 0) || mut.isPending} onClick={() => mut.mutate()} data-testid="topup-submit">
            {mut.isPending ? "Invio…" : "Invia richiesta"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
